import { createContext, useContext, useEffect, useMemo, useState } from "react";

// ------------------------------------------------------------------
// Translation dictionaries.  Keys are dotted strings; arrays supported.
// ------------------------------------------------------------------

const en = {
  "nav.opportunity": "Opportunity",
  "nav.why": "Why Esmeralda",
  "nav.start_conversation": "Start a conversation",
  "nav.contact_short": "Contact",
  "lang.label": "Language",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.de": "Deutsch",

  "hero.eyebrow": "Esmeralda Partners — Venezuela",
  "hero.headline": "Connecting international capital to Venezuela\u2019s real assets.",
  "hero.subheadline": "Livestock and farmland. An early entry.",
  "hero.cta": "Start a conversation",
  "hero.scroll": "Scroll",

  "video.eyebrow": "01 — Positioning",
  "video.headline_main": "A platform built for scale,",
  "video.headline_accent": "not starting from zero.",
  "video.body":
    "Capital joins a working operation: land under management, an active herd, and a team that has been on the ground through every cycle.",
  "video.watch": "Watch · The hacienda",
  "video.close": "Close",
  "metric.years": "Years operating",
  "metric.hectares": "Hectares",
  "metric.operations": "Livestock operations",
  "metric.execution": "Execution",
  "metric.active_prefix": "Active",
  "metric.on_the_ground_prefix": "On-the-ground",

  "opportunity.eyebrow": "02 — Opportunity",
  "opportunity.title": "A rare entry point into a recovering economy.",
  "opportunity.tagline": "Six macro tailwinds",
  "opportunity.1.title": "Macro Stabilization",
  "opportunity.1.desc":
    "Currency reform and dollarization are restoring price signals and capital flows.",
  "opportunity.2.title": "Asset Values",
  "opportunity.2.desc":
    "Land and livestock trade at a fraction of regional benchmarks.",
  "opportunity.3.title": "Early Market Positioning",
  "opportunity.3.desc":
    "Institutional capital has yet to return. First movers set the terms.",
  "opportunity.4.title": "Supply\u2013Demand Imbalance",
  "opportunity.4.desc":
    "Domestic protein demand rebounding faster than productive capacity.",
  "opportunity.5.title": "Agricultural Modernization",
  "opportunity.5.desc":
    "Genetics, pasture systems and logistics gaps ready for upgrade.",
  "opportunity.6.title": "Natural Advantages",
  "opportunity.6.desc":
    "Fertile soils, reliable rainfall, and year-round grazing conditions.",

  "tokenization.divider": "A SIMPLE, TRANSPARENT FIVE-STEP STRUCTURE",
  "tokenization.eyebrow": "03 — Tokenization",
  "tokenization.title": "Invest in Real World Assets",
  "tokenization.subtitle":
    "Access exclusive tokenized investment opportunities backed by real-world value.",
  "tokenization.caption": "A simple, transparent five-step structure.",
  "tokenization.step_label": "STEP",
  "flow.1.title": "Capital",
  "flow.1.desc": "International investors commit funds.",
  "flow.2.title": "Tokenization",
  "flow.2.desc": "Capital is structured into on-chain tokens.",
  "flow.3.title": "Asset Acquisition",
  "flow.3.desc": "Funds acquire livestock and farmland.",
  "flow.4.title": "Biological Growth",
  "flow.4.desc": "Herd matures over the operating cycle.",
  "flow.5.title": "Sales and Payback",
  "flow.5.desc": "Proceeds distributed back to token holders.",

  "token.eyebrow": "Token Offering",
  "token.brickken": "Brickken — Coming Soon",
  "token.tab.overview": "Overview",
  "token.tab.tokenomics": "Tokenomics",
  "token.tab.allocation": "Allocation",
  "token.coming_soon": "Coming Soon",
  "token.label": "Token",
  "token.description":
    "Fractional exposure to the Esmeralda herd via on-chain ownership. Structured for long-horizon investors — distributed yield on-sale, transparent audits on-chain.",
  "token.asset_class": "Asset Class",
  "token.livestock": "Livestock",
  "token.jurisdiction": "Jurisdiction",
  "token.venezuela": "Venezuela",
  "token.total_raised": "Total Raised",
  "token.goal": "Goal",
  "token.raised_suffix": "Raised",
  "token.min_ticket": "Min. ticket · $25,000",
  "token.sale_opens": "Sale opens in",
  "token.cd.days": "days",
  "token.cd.hours": "hours",
  "token.cd.min": "min",
  "token.cd.sec": "sec",
  "token.register": "Register interest",
  "token.whitelist": "Whitelist opens at launch",

  "why.eyebrow": "04 — Conviction",
  "why.title": "Why Esmeralda Partners?",
  "why.body":
    "A grounded approach, with access to assets most institutional capital cannot reach directly.",
  "why.1.title": "Direct asset exposure",
  "why.1.desc":
    "Ownership in tangible, productive land and livestock \u2014 not intermediated vehicles.",
  "why.2.title": "Operators on the ground",
  "why.2.desc":
    "A family operation active in-country for more than three decades.",
  "why.3.title": "Existing platform",
  "why.3.desc":
    "A working hacienda, herd, infrastructure and team \u2014 capital deploys into production.",

  "qa.eyebrow": "05 — Information",
  "qa.title": "Q&A",
  "qa.1.q": "Direct Exposure to Real Assets",
  "qa.1.a":
    "Capital is deployed directly into livestock and farmland operations, not financial abstractions.",
  "qa.2.q": "Operator-Led Execution",
  "qa.2.a":
    "Managed on the ground by a family ranch with over 30 years of continuous operations.",
  "qa.3.q": "Fixed Return Structure",
  "qa.3.a":
    "Debt-based investment model with defined terms and target annual returns of 20\u201330%.",
  "qa.4.q": "Early Market Positioning",
  "qa.4.a":
    "Access assets at a significant discount in a recovering agricultural market.",
  "qa.5.q": "Biological Growth Engine",
  "qa.5.a":
    "Returns driven by reproduction cycles and herd expansion, not market speculation.",
  "qa.6.q": "Transparent Infrastructure",
  "qa.6.a":
    "Structured and tracked through tokenized systems powered by Brickken.",

  "contact.eyebrow": "06 — Contact",
  "contact.title": "Start a Conversation",
  "contact.body":
    "We work with a small number of aligned investors. Leave your details and a principal will reach out personally.",
  "contact.name": "Name",
  "contact.email": "Email",
  "contact.phone": "Phone",
  "contact.name_ph": "Your full name",
  "contact.email_ph": "you@domain.com",
  "contact.phone_ph": "+1 555 555 5555",
  "contact.send": "Send message",
  "contact.sending": "Sending\u2026",
  "contact.success_title": "Thank you. Your message has been received.",
  "contact.success_body":
    "A member of the Esmeralda Partners team will contact you directly at the email or phone number you provided.",
  "contact.reset": "Send another message",
  "contact.toast_success": "Thank you. We will be in touch shortly.",
  "contact.toast_required": "Please complete every field.",
  "contact.toast_generic": "Something went wrong. Please try again.",

  "footer.copyright":
    "\u00A9 {year} Esmeralda Partners \u00B7 Hacienda Esmeralda, Venezuela",
  "footer.about_link": "About",
  "footer.home_link": "Home",

  "about.eyebrow": "About — Esmeralda Partners",
  "about.headline": "Built on decades of real operations.",
  "about.subtext":
    "A family-run livestock operation, now opening to aligned international capital.",
  "about.story_eyebrow": "The story",
  "about.story.1993.label": "1993",
  "about.story.1993.title": "Foundation",
  "about.story.1993.body":
    "Hacienda Esmeralda is founded by the Pérez family with a first herd and a vision for the land.",
  "about.story.growth.label": "Growth",
  "about.story.growth.title": "A working platform",
  "about.story.growth.body":
    "Years of reinvestment build pastures, infrastructure, and a disciplined operating rhythm.",
  "about.story.crisis.label": "Crisis",
  "about.story.crisis.title": "Through the storm",
  "about.story.crisis.body":
    "Through national upheaval, the operation keeps producing \u2014 protecting the herd and the team.",
  "about.story.resilience.label": "Resilience",
  "about.story.resilience.title": "Proof of endurance",
  "about.story.resilience.body":
    "Decades of continuous operation prove an approach grounded in real assets, not speculation.",
  "about.story.today.label": "Today",
  "about.story.today.title": "Opening the gate",
  "about.story.today.body":
    "Esmeralda Partners opens to aligned international capital entering a new phase of recovery.",
  "about.timeline_eyebrow": "Timeline",
  "about.timeline.1993": "Foundation",
  "about.timeline.2005": "Expansion",
  "about.timeline.2015": "Crisis",
  "about.timeline.2020": "Resilience",
  "about.timeline.2026": "Opening",
  "about.team_eyebrow": "Team",
  "about.team_title": "Operators, not intermediaries.",
  "about.back": "Back to homepage",
};

const es = {
  "nav.opportunity": "Oportunidad",
  "nav.why": "Por qu\u00E9 Esmeralda",
  "nav.start_conversation": "Iniciar conversaci\u00F3n",
  "nav.contact_short": "Contacto",
  "lang.label": "Idioma",
  "lang.en": "English",
  "lang.es": "Espa\u00F1ol",
  "lang.de": "Deutsch",

  "hero.eyebrow": "Esmeralda Partners — Venezuela",
  "hero.headline":
    "Conectamos capital internacional con los activos reales de Venezuela.",
  "hero.subheadline": "Ganado y tierra. Una entrada temprana.",
  "hero.cta": "Iniciar conversaci\u00F3n",
  "hero.scroll": "Descender",

  "video.eyebrow": "01 — Posicionamiento",
  "video.headline_main": "Una plataforma pensada para escalar,",
  "video.headline_accent": "no para empezar desde cero.",
  "video.body":
    "El capital se suma a una operaci\u00F3n en marcha: tierra en producci\u00F3n, un hato activo y un equipo con presencia en el campo a lo largo de cada ciclo.",
  "video.watch": "Ver · La hacienda",
  "video.close": "Cerrar",
  "metric.years": "A\u00F1os operando",
  "metric.hectares": "Hect\u00E1reas",
  "metric.operations": "Operaciones ganaderas",
  "metric.execution": "Ejecuci\u00F3n",
  "metric.active_prefix": "Activas",
  "metric.on_the_ground_prefix": "En el terreno",

  "opportunity.eyebrow": "02 — Oportunidad",
  "opportunity.title":
    "Un punto de entrada poco com\u00FAn a una econom\u00EDa en recuperaci\u00F3n.",
  "opportunity.tagline": "Seis vientos de cola macro",
  "opportunity.1.title": "Estabilizaci\u00F3n macro",
  "opportunity.1.desc":
    "La reforma cambiaria y la dolarizaci\u00F3n est\u00E1n restaurando precios y flujos de capital.",
  "opportunity.2.title": "Valor de los activos",
  "opportunity.2.desc":
    "La tierra y el ganado cotizan a una fracci\u00F3n de los referentes regionales.",
  "opportunity.3.title": "Posicionamiento temprano",
  "opportunity.3.desc":
    "El capital institucional a\u00FAn no regresa. Quienes llegan primero fijan los t\u00E9rminos.",
  "opportunity.4.title": "Desequilibrio oferta\u2013demanda",
  "opportunity.4.desc":
    "La demanda interna de prote\u00EDna se recupera m\u00E1s r\u00E1pido que la capacidad productiva.",
  "opportunity.5.title": "Modernizaci\u00F3n agr\u00EDcola",
  "opportunity.5.desc":
    "Gen\u00E9tica, pastizales y log\u00EDstica preparados para ser actualizados.",
  "opportunity.6.title": "Ventajas naturales",
  "opportunity.6.desc":
    "Suelos f\u00E9rtiles, lluvias confiables y pastoreo todo el a\u00F1o.",

  "tokenization.divider": "UNA ESTRUCTURA SIMPLE Y TRANSPARENTE DE CINCO PASOS",
  "tokenization.eyebrow": "03 — Tokenizaci\u00F3n",
  "tokenization.title": "Invierta en activos del mundo real",
  "tokenization.subtitle":
    "Acceda a oportunidades de inversi\u00F3n tokenizadas respaldadas por activos reales.",
  "tokenization.caption": "Una estructura simple y transparente de cinco pasos.",
  "tokenization.step_label": "PASO",
  "flow.1.title": "Capital",
  "flow.1.desc": "Inversionistas internacionales comprometen fondos.",
  "flow.2.title": "Tokenizaci\u00F3n",
  "flow.2.desc": "El capital se estructura en tokens en cadena.",
  "flow.3.title": "Adquisici\u00F3n de activos",
  "flow.3.desc": "Los fondos adquieren ganado y tierra.",
  "flow.4.title": "Crecimiento biol\u00F3gico",
  "flow.4.desc": "El hato madura durante el ciclo operativo.",
  "flow.5.title": "Ventas y retorno",
  "flow.5.desc":
    "Los ingresos se distribuyen a los titulares del token.",

  "token.eyebrow": "Oferta del token",
  "token.brickken": "Brickken — Pronto",
  "token.tab.overview": "Resumen",
  "token.tab.tokenomics": "Tokenomics",
  "token.tab.allocation": "Asignaci\u00F3n",
  "token.coming_soon": "Pronto",
  "token.label": "Token",
  "token.description":
    "Exposici\u00F3n fraccionada al hato Esmeralda mediante propiedad en cadena. Dise\u00F1ado para inversionistas de largo plazo \u2014 rendimiento distribuido al momento de la venta, auditor\u00EDas on-chain.",
  "token.asset_class": "Clase de activo",
  "token.livestock": "Ganader\u00EDa",
  "token.jurisdiction": "Jurisdicci\u00F3n",
  "token.venezuela": "Venezuela",
  "token.total_raised": "Recaudado",
  "token.goal": "Objetivo",
  "token.raised_suffix": "recaudado",
  "token.min_ticket": "Ticket m\u00EDnimo · USD 25.000",
  "token.sale_opens": "La venta abre en",
  "token.cd.days": "d\u00EDas",
  "token.cd.hours": "horas",
  "token.cd.min": "min",
  "token.cd.sec": "seg",
  "token.register": "Registrar inter\u00E9s",
  "token.whitelist": "La lista blanca abre al lanzamiento",

  "why.eyebrow": "04 — Convicci\u00F3n",
  "why.title": "\u00BFPor qu\u00E9 Esmeralda Partners?",
  "why.body":
    "Un enfoque con los pies en la tierra y acceso a activos a los que el capital institucional rara vez llega directamente.",
  "why.1.title": "Exposici\u00F3n directa al activo",
  "why.1.desc":
    "Propiedad en tierra y ganado productivos \u2014 no veh\u00EDculos intermediados.",
  "why.2.title": "Operadores en el terreno",
  "why.2.desc":
    "Una operaci\u00F3n familiar activa en el pa\u00EDs por m\u00E1s de tres d\u00E9cadas.",
  "why.3.title": "Plataforma existente",
  "why.3.desc":
    "Una hacienda en marcha, hato, infraestructura y equipo \u2014 el capital entra directo en producci\u00F3n.",

  "qa.eyebrow": "05 — Informaci\u00F3n",
  "qa.title": "Preguntas y respuestas",
  "qa.1.q": "Exposici\u00F3n directa a activos reales",
  "qa.1.a":
    "El capital se destina directamente a operaciones de ganado y tierra, no a abstracciones financieras.",
  "qa.2.q": "Ejecuci\u00F3n liderada por operadores",
  "qa.2.a":
    "Gestionada en el terreno por una hacienda familiar con m\u00E1s de 30 a\u00F1os de operaciones continuas.",
  "qa.3.q": "Estructura de retorno fija",
  "qa.3.a":
    "Modelo de inversi\u00F3n basado en deuda, con t\u00E9rminos definidos y retornos anuales objetivo del 20\u201330%.",
  "qa.4.q": "Posicionamiento temprano",
  "qa.4.a":
    "Acceso a activos con un descuento significativo en un mercado agr\u00EDcola en recuperaci\u00F3n.",
  "qa.5.q": "Motor de crecimiento biol\u00F3gico",
  "qa.5.a":
    "Retornos impulsados por los ciclos de reproducci\u00F3n y la expansi\u00F3n del hato, no por la especulaci\u00F3n de mercado.",
  "qa.6.q": "Infraestructura transparente",
  "qa.6.a":
    "Estructurada y auditada mediante sistemas tokenizados impulsados por Brickken.",

  "contact.eyebrow": "06 — Contacto",
  "contact.title": "Iniciar una conversaci\u00F3n",
  "contact.body":
    "Trabajamos con un n\u00FAmero acotado de inversionistas alineados. D\u00E9jenos sus datos y un socio se pondr\u00E1 en contacto personalmente.",
  "contact.name": "Nombre",
  "contact.email": "Correo",
  "contact.phone": "Tel\u00E9fono",
  "contact.name_ph": "Su nombre completo",
  "contact.email_ph": "usted@dominio.com",
  "contact.phone_ph": "+58 000 000 0000",
  "contact.send": "Enviar mensaje",
  "contact.sending": "Enviando\u2026",
  "contact.success_title": "Gracias. Hemos recibido su mensaje.",
  "contact.success_body":
    "Un miembro del equipo de Esmeralda Partners le contactar\u00E1 directamente al correo o tel\u00E9fono proporcionado.",
  "contact.reset": "Enviar otro mensaje",
  "contact.toast_success": "Gracias. Estaremos en contacto pronto.",
  "contact.toast_required": "Por favor complete todos los campos.",
  "contact.toast_generic":
    "Ocurri\u00F3 un inconveniente. Intente nuevamente.",

  "footer.copyright":
    "\u00A9 {year} Esmeralda Partners \u00B7 Hacienda Esmeralda, Venezuela",
  "footer.about_link": "Sobre nosotros",
  "footer.home_link": "Inicio",

  "about.eyebrow": "Sobre nosotros — Esmeralda Partners",
  "about.headline": "Construido sobre d\u00E9cadas de operaciones reales.",
  "about.subtext":
    "Una operaci\u00F3n ganadera familiar que abre ahora sus puertas al capital internacional alineado.",
  "about.story_eyebrow": "La historia",
  "about.story.1993.label": "1993",
  "about.story.1993.title": "Fundaci\u00F3n",
  "about.story.1993.body":
    "La familia P\u00E9rez funda Hacienda Esmeralda con un primer hato y una visi\u00F3n para la tierra.",
  "about.story.growth.label": "Crecimiento",
  "about.story.growth.title": "Una plataforma en marcha",
  "about.story.growth.body":
    "A\u00F1os de reinversi\u00F3n construyen pasturas, infraestructura y una disciplina operativa s\u00F3lida.",
  "about.story.crisis.label": "Crisis",
  "about.story.crisis.title": "A trav\u00E9s de la tormenta",
  "about.story.crisis.body":
    "En medio de la crisis nacional, la operaci\u00F3n sigue produciendo \u2014 protegiendo al hato y al equipo.",
  "about.story.resilience.label": "Resiliencia",
  "about.story.resilience.title": "Prueba de permanencia",
  "about.story.resilience.body":
    "D\u00E9cadas de operaci\u00F3n continua demuestran un enfoque anclado en activos reales, no en la especulaci\u00F3n.",
  "about.story.today.label": "Hoy",
  "about.story.today.title": "Abriendo la tranquera",
  "about.story.today.body":
    "Esmeralda Partners se abre a capital internacional alineado en una nueva fase de recuperaci\u00F3n.",
  "about.timeline_eyebrow": "Cronolog\u00EDa",
  "about.timeline.1993": "Fundaci\u00F3n",
  "about.timeline.2005": "Expansi\u00F3n",
  "about.timeline.2015": "Crisis",
  "about.timeline.2020": "Resiliencia",
  "about.timeline.2026": "Apertura",
  "about.team_eyebrow": "Equipo",
  "about.team_title": "Operadores, no intermediarios.",
  "about.back": "Volver al inicio",
};

const de = {
  "nav.opportunity": "Chance",
  "nav.why": "Warum Esmeralda",
  "nav.start_conversation": "Gespr\u00E4ch beginnen",
  "nav.contact_short": "Kontakt",
  "lang.label": "Sprache",
  "lang.en": "English",
  "lang.es": "Espa\u00F1ol",
  "lang.de": "Deutsch",

  "hero.eyebrow": "Esmeralda Partners — Venezuela",
  "hero.headline":
    "Internationales Kapital trifft auf Venezuelas reale Verm\u00F6genswerte.",
  "hero.subheadline": "Viehzucht und Ackerland. Ein fr\u00FCher Einstieg.",
  "hero.cta": "Gespr\u00E4ch beginnen",
  "hero.scroll": "Scrollen",

  "video.eyebrow": "01 — Positionierung",
  "video.headline_main": "Eine Plattform, gebaut zum Skalieren,",
  "video.headline_accent": "nicht zum Anfangen bei Null.",
  "video.body":
    "Kapital flie\u00DFt in einen laufenden Betrieb: bewirtschaftetes Land, eine aktive Herde und ein Team, das in jedem Zyklus vor Ort war.",
  "video.watch": "Ansehen · Die Hacienda",
  "video.close": "Schlie\u00DFen",
  "metric.years": "Jahre in Betrieb",
  "metric.hectares": "Hektar",
  "metric.operations": "Viehzuchtbetriebe",
  "metric.execution": "Umsetzung",
  "metric.active_prefix": "Aktive",
  "metric.on_the_ground_prefix": "Vor-Ort-",

  "opportunity.eyebrow": "02 — Chance",
  "opportunity.title":
    "Ein seltener Einstiegspunkt in eine sich erholende Wirtschaft.",
  "opportunity.tagline": "Sechs Makro-R\u00FCckenwinde",
  "opportunity.1.title": "Makrostabilisierung",
  "opportunity.1.desc":
    "W\u00E4hrungsreform und Dollarisierung stellen Preissignale und Kapitalstr\u00F6me wieder her.",
  "opportunity.2.title": "Verm\u00F6genswerte",
  "opportunity.2.desc":
    "Land und Vieh werden zu einem Bruchteil regionaler Benchmarks gehandelt.",
  "opportunity.3.title": "Fr\u00FChe Marktpositionierung",
  "opportunity.3.desc":
    "Institutionelles Kapital ist noch nicht zur\u00FCck. First Mover setzen die Konditionen.",
  "opportunity.4.title": "Angebot\u2013Nachfrage-Ungleichgewicht",
  "opportunity.4.desc":
    "Die inl\u00E4ndische Proteinnachfrage w\u00E4chst schneller als die Produktionskapazit\u00E4t.",
  "opportunity.5.title": "Modernisierung der Landwirtschaft",
  "opportunity.5.desc":
    "Genetik, Weidesysteme und Logistik sind reif f\u00FCr ein Upgrade.",
  "opportunity.6.title": "Nat\u00FCrliche Vorteile",
  "opportunity.6.desc":
    "Fruchtbare B\u00F6den, zuverl\u00E4ssiger Niederschlag und ganzj\u00E4hrige Weide.",

  "tokenization.divider":
    "EINE EINFACHE, TRANSPARENTE F\u00DCNF-STUFEN-STRUKTUR",
  "tokenization.eyebrow": "03 — Tokenisierung",
  "tokenization.title": "In reale Verm\u00F6genswerte investieren",
  "tokenization.subtitle":
    "Zugang zu tokenisierten Anlagen, die durch reale Werte gedeckt sind.",
  "tokenization.caption": "Eine einfache, transparente F\u00FCnf-Stufen-Struktur.",
  "tokenization.step_label": "SCHRITT",
  "flow.1.title": "Kapital",
  "flow.1.desc": "Internationale Investoren binden Mittel.",
  "flow.2.title": "Tokenisierung",
  "flow.2.desc": "Kapital wird in On-Chain-Token strukturiert.",
  "flow.3.title": "Asset-Erwerb",
  "flow.3.desc": "Mittel erwerben Vieh und Ackerland.",
  "flow.4.title": "Biologisches Wachstum",
  "flow.4.desc": "Die Herde reift im operativen Zyklus.",
  "flow.5.title": "Verkauf und R\u00FCckzahlung",
  "flow.5.desc":
    "Erl\u00F6se werden an die Token-Inhaber ausgesch\u00FCttet.",

  "token.eyebrow": "Token-Angebot",
  "token.brickken": "Brickken — In K\u00FCrze",
  "token.tab.overview": "\u00DCbersicht",
  "token.tab.tokenomics": "Tokenomics",
  "token.tab.allocation": "Allokation",
  "token.coming_soon": "In K\u00FCrze",
  "token.label": "Token",
  "token.description":
    "Anteilige Beteiligung an der Esmeralda-Herde \u00FCber On-Chain-Eigentum. Strukturiert f\u00FCr langfristige Investoren \u2014 Ertr\u00E4ge bei Verkauf, transparente On-Chain-Audits.",
  "token.asset_class": "Anlageklasse",
  "token.livestock": "Viehzucht",
  "token.jurisdiction": "Jurisdiktion",
  "token.venezuela": "Venezuela",
  "token.total_raised": "Eingesammelt",
  "token.goal": "Ziel",
  "token.raised_suffix": "eingesammelt",
  "token.min_ticket": "Mindestticket · USD 25.000",
  "token.sale_opens": "Verkauf beginnt in",
  "token.cd.days": "Tage",
  "token.cd.hours": "Std",
  "token.cd.min": "Min",
  "token.cd.sec": "Sek",
  "token.register": "Interesse bekunden",
  "token.whitelist": "Whitelist \u00F6ffnet zum Start",

  "why.eyebrow": "04 — \u00DCberzeugung",
  "why.title": "Warum Esmeralda Partners?",
  "why.body":
    "Ein bodenst\u00E4ndiger Ansatz mit Zugang zu Verm\u00F6genswerten, die institutionelles Kapital direkt selten erreicht.",
  "why.1.title": "Direkte Asset-Exposition",
  "why.1.desc":
    "Eigentum an produktivem Land und Vieh \u2014 keine zwischengeschalteten Vehikel.",
  "why.2.title": "Betreiber vor Ort",
  "why.2.desc":
    "Eine Familienoperation, seit mehr als drei Jahrzehnten im Land aktiv.",
  "why.3.title": "Bestehende Plattform",
  "why.3.desc":
    "Eine arbeitende Hacienda, Herde, Infrastruktur und Team \u2014 Kapital flie\u00DFt direkt in die Produktion.",

  "qa.eyebrow": "05 — Information",
  "qa.title": "Fragen & Antworten",
  "qa.1.q": "Direkte Exposition zu realen Assets",
  "qa.1.a":
    "Kapital wird direkt in Vieh- und Ackerlandbetriebe investiert \u2014 nicht in Finanzabstraktionen.",
  "qa.2.q": "Operativ gef\u00FChrte Umsetzung",
  "qa.2.a":
    "Vor Ort gef\u00FChrt von einer Familienfarm mit \u00FCber 30 Jahren Dauerbetrieb.",
  "qa.3.q": "Fixe Renditestruktur",
  "qa.3.a":
    "Fremdkapitalbasiertes Modell mit definierten Konditionen und Ziel-Jahresrenditen von 20\u201330%.",
  "qa.4.q": "Fr\u00FChe Marktpositionierung",
  "qa.4.a":
    "Zugang zu Assets mit deutlichem Abschlag in einem sich erholenden Agrarmarkt.",
  "qa.5.q": "Biologische Wachstumsmaschine",
  "qa.5.a":
    "Ertr\u00E4ge aus Reproduktionszyklen und Herdenwachstum \u2014 keine Marktspekulation.",
  "qa.6.q": "Transparente Infrastruktur",
  "qa.6.a":
    "Strukturiert und abgebildet \u00FCber tokenisierte Systeme von Brickken.",

  "contact.eyebrow": "06 — Kontakt",
  "contact.title": "Gespr\u00E4ch beginnen",
  "contact.body":
    "Wir arbeiten mit einer kleinen Anzahl abgestimmter Investoren. Hinterlassen Sie Ihre Daten \u2014 ein Partner meldet sich pers\u00F6nlich.",
  "contact.name": "Name",
  "contact.email": "E-Mail",
  "contact.phone": "Telefon",
  "contact.name_ph": "Ihr vollst\u00E4ndiger Name",
  "contact.email_ph": "sie@domain.com",
  "contact.phone_ph": "+49 000 0000000",
  "contact.send": "Nachricht senden",
  "contact.sending": "Senden\u2026",
  "contact.success_title": "Danke. Ihre Nachricht ist eingegangen.",
  "contact.success_body":
    "Ein Mitglied von Esmeralda Partners meldet sich direkt unter der angegebenen E-Mail oder Telefonnummer.",
  "contact.reset": "Weitere Nachricht senden",
  "contact.toast_success": "Danke. Wir melden uns in K\u00FCrze.",
  "contact.toast_required": "Bitte alle Felder ausf\u00FCllen.",
  "contact.toast_generic":
    "Etwas ist schiefgelaufen. Bitte erneut versuchen.",

  "footer.copyright":
    "\u00A9 {year} Esmeralda Partners \u00B7 Hacienda Esmeralda, Venezuela",
  "footer.about_link": "\u00DCber uns",
  "footer.home_link": "Startseite",

  "about.eyebrow": "\u00DCber uns — Esmeralda Partners",
  "about.headline": "Getragen von Jahrzehnten realer Operationen.",
  "about.subtext":
    "Ein Familienbetrieb im Viehsektor, der sich nun abgestimmtem internationalem Kapital \u00F6ffnet.",
  "about.story_eyebrow": "Die Geschichte",
  "about.story.1993.label": "1993",
  "about.story.1993.title": "Gr\u00FCndung",
  "about.story.1993.body":
    "Die Familie P\u00E9rez gr\u00FCndet die Hacienda Esmeralda mit einer ersten Herde und einer klaren Vision.",
  "about.story.growth.label": "Wachstum",
  "about.story.growth.title": "Eine arbeitende Plattform",
  "about.story.growth.body":
    "Jahre der Reinvestition schaffen Weiden, Infrastruktur und einen disziplinierten Betriebsrhythmus.",
  "about.story.crisis.label": "Krise",
  "about.story.crisis.title": "Durch den Sturm",
  "about.story.crisis.body":
    "W\u00E4hrend der nationalen Krise produziert der Betrieb weiter \u2014 Herde und Team werden gesch\u00FCtzt.",
  "about.story.resilience.label": "Widerstandsf\u00E4higkeit",
  "about.story.resilience.title": "Beleg f\u00FCr Best\u00E4ndigkeit",
  "about.story.resilience.body":
    "Jahrzehnte ununterbrochenen Betriebs beweisen einen Ansatz auf Basis realer Werte \u2014 nicht Spekulation.",
  "about.story.today.label": "Heute",
  "about.story.today.title": "Das Tor \u00F6ffnet sich",
  "about.story.today.body":
    "Esmeralda Partners \u00F6ffnet sich abgestimmtem internationalem Kapital in einer neuen Erholungsphase.",
  "about.timeline_eyebrow": "Zeitstrahl",
  "about.timeline.1993": "Gr\u00FCndung",
  "about.timeline.2005": "Expansion",
  "about.timeline.2015": "Krise",
  "about.timeline.2020": "Widerstandsf\u00E4higkeit",
  "about.timeline.2026": "\u00D6ffnung",
  "about.team_eyebrow": "Team",
  "about.team_title": "Betreiber, keine Vermittler.",
  "about.back": "Zur\u00FCck zur Startseite",
};

const DICTIONARIES = { en, es, de };
export const LOCALES = [
  { code: "en", labelKey: "lang.en", short: "EN" },
  { code: "es", labelKey: "lang.es", short: "ES" },
  { code: "de", labelKey: "lang.de", short: "DE" },
];

const STORAGE_KEY = "esmeralda.lang";

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && DICTIONARIES[saved]) return saved;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return DICTIONARIES[nav] ? nav : "en";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(() => {
    const dict = DICTIONARIES[lang] || en;
    const t = (key, vars) => {
      const raw = dict[key] ?? en[key] ?? key;
      if (!vars) return raw;
      return Object.keys(vars).reduce(
        (acc, k) => acc.replaceAll(`{${k}}`, vars[k]),
        raw
      );
    };
    return { lang, setLang: setLangState, t };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useT = () => useContext(I18nContext);
